import { motion } from 'framer-motion';
import { Clock, MapPin, Plus } from 'lucide-react';
import React from 'react';

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

const CalendarGrid = ({
  currentDate,
  view,
  events,
  employees,
  onDateClick,
  onEventClick,
  onCreateEvent
}) => {


  const getEmployeeNames = (employeeIds) => {
    return employeeIds.map(id => {
      const employee = employees.find(e => e.id === id);
      return employee ? employee.name : 'Unknown';
    });
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const getEventColor = (event) => {
    const colors = {
      primary: 'bg-primary-500',
      secondary: 'bg-secondary-500',
      accent: 'bg-accent-500',
      warning: 'bg-warning-500',
      success: 'bg-success-500'
    };
    return colors[event.color] || colors.primary;
  };

  // Month View
  const renderMonthView = () => {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

    const days = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return (
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">
            {day}
          </div>
        ))}

        {/* Days */}
        {days.map((day, index) => {
          const dayEvents = getEventsForDate(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          const isToday = day.toDateString() === new Date().toDateString();

          return (
            <motion.div
              key={index}
              className={`bg-white min-h-[120px] p-2 cursor-pointer hover:bg-gray-50 transition-colors relative group ${
                !isCurrentMonth ? 'bg-gray-50' : ''
              }`}
              onClick={() => onDateClick(day.toISOString().split('T')[0])}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Create Event Button */}
              {isCurrentMonth && (
                <motion.button
                  className="absolute top-1 right-1 w-6 h-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateEvent(day.toISOString().split('T')[0]);
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Create new event"
                >
                  <Plus size={12} />
                </motion.button>
              )}

              {/* Date Header */}
              <div className={`text-sm font-medium mb-2 ${
                isToday ? 'text-primary-600 font-bold' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
              }`}>
                {day.getDate()}
              </div>

              {/* Events List */}
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event, eventIndex) => (
                  <motion.div
                    key={event.id}
                    className={`text-xs p-1 rounded text-white truncate ${getEventColor(event)} cursor-pointer`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title={`${event.title} - ${event.time} ${event.location ? `at ${event.location}` : ''}`}
                  >
                    <div className="flex items-center">
                      <Clock size={10} className="mr-1 flex-shrink-0" />
                      <span className="truncate">{event.title}</span>
                    </div>
                  </motion.div>
                ))}

                {dayEvents.length > 3 && (
                  <div className="text-xs text-gray-500">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>

              {/* Hover overlay to show create button */}
              {isCurrentMonth && (
                <div className="absolute inset-0 bg-transparent group opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
              )}
            </motion.div>
          );
        })}
      </div>
    );
  };

  // Week View
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }

    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Header */}
          <div className="grid grid-cols-8 gap-px bg-gray-200">
            <div className="bg-gray-50 p-3"></div>
            {weekDays.map((day, index) => {
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div key={index} className={`bg-gray-50 p-3 text-center ${isToday ? 'bg-primary-50' : ''}`}>
                  <div className={`text-sm font-medium ${isToday ? 'text-primary-600' : 'text-gray-700'}`}>
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className={`text-lg font-bold ${isToday ? 'text-primary-600' : 'text-gray-900'}`}>
                    {day.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Slots */}
          <div className="grid grid-cols-8 gap-px bg-gray-200">
            {hours.map(hour => (
              <React.Fragment key={hour}>
                {/* Time Label */}
                <div className="bg-white p-2 text-xs text-gray-500 border-r">
                  {hour.toString().padStart(2, '0')}:00
                </div>

                {/* Day Columns */}
                {weekDays.map((day, dayIndex) => {
                  const dayEvents = getEventsForDate(day).filter(event => {
                    const eventHour = parseInt(event.time.split(':')[0]);
                    return eventHour === hour;
                  });

                  return (
                    <motion.div
                      key={dayIndex}
                      className="bg-white min-h-[60px] p-1 cursor-pointer hover:bg-gray-50 transition-colors relative"
                      onClick={() => onDateClick(day.toISOString().split('T')[0])}
                      variants={itemVariants}
                    >
                      {dayEvents.map((event, eventIndex) => (
                        <motion.div
                          key={event.id}
                          className={`text-xs p-2 rounded-lg text-white mb-1 ${getEventColor(event)} cursor-pointer`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          title={`${event.title} - ${event.time} ${event.location ? `at ${event.location}` : ''}`}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          <div className="text-xs opacity-90">{event.time}</div>
                        </motion.div>
                      ))}
                    </motion.div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Day View
  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForDate(currentDate);

    return (
      <div className="space-y-2">
        {hours.map(hour => {
          const hourEvents = dayEvents.filter(event => {
            const eventHour = parseInt(event.time.split(':')[0]);
            return eventHour === hour;
          });

          return (
            <motion.div
              key={hour}
              className="flex border-b border-gray-100 pb-2"
              variants={itemVariants}
            >
              <div className="w-20 text-sm text-gray-500 pt-2">
                {hour.toString().padStart(2, '0')}:00
              </div>

              <motion.div
                className="flex-1 min-h-[80px] p-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => onDateClick(currentDate.toISOString().split('T')[0])}
                whileHover={{ scale: 1.01 }}
              >
                <div className="space-y-2">
                  {hourEvents.map((event, eventIndex) => (
                    <motion.div
                      key={event.id}
                      className={`p-3 rounded-lg text-white cursor-pointer ${getEventColor(event)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{event.title}</h4>
                          <p className="text-xs opacity-90 mt-1">{event.description}</p>

                          <div className="flex items-center mt-2 text-xs opacity-90">
                            <Clock size={12} className="mr-1" />
                            <span>{event.time} - {event.endTime}</span>
                            {event.location && (
                              <>
                                <span className="mx-2">•</span>
                                <MapPin size={12} className="mr-1" />
                                <span>{event.location}</span>
                              </>
                            )}
                          </div>

                          {event.taggedEmployees.length > 0 && (
                            <div className="mt-2 text-xs opacity-90">
                              Tagged: {getEmployeeNames(event.taggedEmployees).join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    );
  };

  const renderView = () => {
    switch (view) {
      case 'week':
        return renderWeekView();
      case 'day':
        return renderDayView();
      default:
        return renderMonthView();
    }
  };

  return (
    <motion.div
      className="p-6"
      variants={itemVariants}
      initial="hidden"
      animate="visible"
    >
      {renderView()}
    </motion.div>
  );
};

export default CalendarGrid;
